import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider,
  Button,
  Paper,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  ShoppingCart as ShoppingCartIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useShopifyCart } from '../../hooks/useShopifyCart';

interface CartSidebarProps {
  open: boolean;
  onClose: () => void;
}

export const CartSidebar: React.FC<CartSidebarProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const { cart, isLoading, itemCount, updateQuantity, removeItem } = useShopifyCart();

  const handleQuantityChange = async (lineItemId: string, currentQuantity: number, change: number) => {
    const newQuantity = currentQuantity + change;
    if (newQuantity >= 0) {
      await updateQuantity(lineItemId, newQuantity);
    }
  };

  const handleRemoveItem = async (lineItemId: string) => {
    await removeItem(lineItemId);
  };

  const handleCheckout = () => {
    if (!cart?.checkoutUrl) return;

    // Check if cart contains professional installation product
    const hasProfessionalInstall = cart.lineItems.some(item => 
      item.variant.id === 'gid://shopify/ProductVariant/47847606157451' || // GraphQL format
      item.variant.id === '47847606157451' || // Numeric format
      item.variant.id.includes('47847606157451') || // Contains the numeric ID
      item.product.title.toLowerCase().includes('professionally installed')
    );

    if (hasProfessionalInstall) {
      // Store info for potential manual follow-up (backup)
      const customerInfo = {
        timestamp: Date.now(),
        cart: cart,
        hasProfessionalInstall: true
      };
      localStorage.setItem('haven_checkout_info', JSON.stringify(customerInfo));

      // Note: Shopify often ignores return_url for security reasons
      // Primary follow-up will be via webhook → email after purchase
      // This return_url is a fallback attempt only
      const returnUrl = `${window.location.origin}/order-confirmation?source=checkout`;
      const checkoutUrlWithReturn = `${cart.checkoutUrl}&return_url=${encodeURIComponent(returnUrl)}`;
      
      window.open(checkoutUrlWithReturn, '_blank');
    } else {
      // Regular checkout for non-professional products
      window.open(cart.checkoutUrl, '_blank');
    }
  };

  const formatPrice = (amount: string, currencyCode: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
    }).format(parseFloat(amount));
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 400 },
          maxWidth: '100vw',
        },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box
          sx={{
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShoppingCartIcon color="primary" />
            <Typography variant="h6" component="h2">
              Shopping Cart
            </Typography>
            {itemCount > 0 && (
              <Chip
                label={itemCount}
                size="small"
                color="primary"
                sx={{ ml: 1 }}
              />
            )}
          </Box>
          <IconButton onClick={onClose} edge="end">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Cart Content */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {!cart || cart.lineItems.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: 4,
                textAlign: 'center',
                minHeight: 200,
              }}
            >
              <ShoppingCartIcon
                sx={{
                  fontSize: 64,
                  color: 'grey.400',
                  mb: 2,
                }}
              />
              <Typography variant="h6" color="textSecondary" gutterBottom>
                Your cart is empty
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Add some products to get started
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {cart.lineItems.map((item, index) => (
                <React.Fragment key={item.id}>
                  <ListItem
                    sx={{
                      flexDirection: 'column',
                      alignItems: 'stretch',
                      p: 2,
                    }}
                  >
                    <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                      <ListItemAvatar>
                        <Avatar
                          src={item.variant.image?.url}
                          alt={item.product.title}
                          variant="rounded"
                          sx={{ width: 60, height: 60 }}
                        />
                      </ListItemAvatar>
                      
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <ListItemText
                          primary={item.product.title}
                          secondary={item.variant.title !== 'Default Title' ? item.variant.title : ''}
                          primaryTypographyProps={{
                            variant: 'subtitle2',
                            fontWeight: 'medium',
                            noWrap: true,
                          }}
                          secondaryTypographyProps={{
                            variant: 'body2',
                            color: 'text.secondary',
                          }}
                        />
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                          <Typography variant="body2" fontWeight="medium">
                            {formatPrice(item.variant.price.amount, item.variant.price.currencyCode)}
                          </Typography>
                          {item.variant.compareAtPrice && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ textDecoration: 'line-through' }}
                            >
                              {formatPrice(item.variant.compareAtPrice.amount, item.variant.compareAtPrice.currencyCode)}
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      <IconButton
                        onClick={() => handleRemoveItem(item.id)}
                        size="small"
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    {/* Quantity Controls */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mt: 2,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton
                          onClick={() => handleQuantityChange(item.id, item.quantity, -1)}
                          size="small"
                          disabled={isLoading}
                        >
                          <RemoveIcon />
                        </IconButton>
                        <Typography
                          variant="body2"
                          sx={{
                            mx: 2,
                            minWidth: 24,
                            textAlign: 'center',
                            fontWeight: 'medium',
                          }}
                        >
                          {item.quantity}
                        </Typography>
                        <IconButton
                          onClick={() => handleQuantityChange(item.id, item.quantity, 1)}
                          size="small"
                          disabled={isLoading}
                        >
                          <AddIcon />
                        </IconButton>
                      </Box>

                      <Typography variant="body2" fontWeight="medium">
                        {formatPrice(
                          (parseFloat(item.variant.price.amount) * item.quantity).toString(),
                          item.variant.price.currencyCode
                        )}
                      </Typography>
                    </Box>
                  </ListItem>
                  
                  {index < cart.lineItems.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>

        {/* Footer with Total and Checkout */}
        {cart && cart.lineItems.length > 0 && (
          <Paper
            elevation={3}
            sx={{
              p: 2,
              borderRadius: 0,
              borderTop: 1,
              borderColor: 'divider',
            }}
          >
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Subtotal
                </Typography>
                <Typography variant="body2">
                  {formatPrice(cart.subtotalPrice.amount, cart.subtotalPrice.currencyCode)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="h6" fontWeight="bold">
                  Total
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="primary">
                  {formatPrice(cart.totalPrice.amount, cart.totalPrice.currencyCode)}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                Shipping and taxes calculated at checkout
              </Typography>
            </Box>

            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleCheckout}
              disabled={isLoading}
              sx={{
                backgroundColor: theme.palette.secondary.main,
                color: 'black',
                fontWeight: 'bold',
                '&:hover': {
                  backgroundColor: theme.palette.secondary.dark,
                },
              }}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Proceed to Checkout'
              )}
            </Button>
          </Paper>
        )}
      </Box>
    </Drawer>
  );
};

export default CartSidebar; 